import { Navigate, useParams } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { SERVICES } from "../data/services";
import { Reveal } from "../components/site/Reveal";
import { MagneticButton } from "../components/site/MagneticButton";
import { useCalendly } from "../components/site/CalendlyModal";

const FlowVisual = ({ steps }) => (
  <div className="flex flex-wrap items-center gap-2.5" data-testid="service-flow">
    {steps.map((s, i) => (
      <Reveal key={s} delay={i * 0.1} className="flex items-center gap-2.5">
        <span className={`mono rounded-xl border px-4 py-2.5 text-xs tracking-wide ${i === steps.length - 1 ? "border-[var(--green)]/60 bg-[var(--green)]/10 text-[var(--green)]" : "border-[var(--line)] bg-[var(--surface)] text-[var(--ink)]/85"}`}>
          {s}
        </span>
        {i < steps.length - 1 && <ArrowRight size={14} className="text-[var(--cyan)]" aria-hidden="true" />}
      </Reveal>
    ))}
  </div>
);

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const { openCalendly } = useCalendly();

  if (!service) return <Navigate to="/" replace />;

  return (
    <div data-testid={`service-page-${service.slug}`}>
      <section className="relative overflow-hidden pt-44 pb-20 grain">
        <div className="absolute -top-32 right-0 w-[480px] h-[480px] rounded-full bg-[var(--blue)]/8 blur-[140px]" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="mono text-xs tracking-[0.4em] text-[var(--green)] mb-6">SOLUCIÓN / {service.num}</p>
          </Reveal>
          <div className="relative">
            <span className="pointer-events-none absolute -top-16 right-0 mono text-[10rem] md:text-[16rem] font-medium leading-none text-transparent opacity-40 select-none" style={{ WebkitTextStroke: "1px rgba(145,163,181,0.18)" }} aria-hidden="true">
              {service.num}
            </span>
            <Reveal delay={0.08}>
              <h1 className="relative max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight" data-testid="service-title">
                {service.title}
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="relative mt-6 text-xl md:text-2xl font-semibold text-gradient max-w-2xl">{service.tagline}</p>
            </Reveal>
            <Reveal delay={0.24}>
              <p className="relative mt-6 max-w-2xl text-base md:text-lg text-[var(--muted)] leading-relaxed">{service.description}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="relative mt-9 flex flex-wrap gap-4">
                <MagneticButton onClick={openCalendly} testId="service-cta-agendar">Agendar una reunión</MagneticButton>
                <MagneticButton variant="ghost" to="/contacto" testId="service-cta-contacto">Hablar con un especialista</MagneticButton>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[var(--bg2)]">
        <div className="mx-auto max-w-7xl px-6 grid gap-14 lg:grid-cols-2">
          <div>
            <Reveal>
              <h2 className="mono text-xs tracking-[0.35em] uppercase text-[var(--green)] mb-8">Qué resolvemos</h2>
            </Reveal>
            <ul className="space-y-4">
              {service.points.map((p, i) => (
                <Reveal key={p} delay={i * 0.08}>
                  <li className="flex items-start gap-3.5 rounded-xl border border-[var(--line)] bg-[var(--surface)]/60 px-5 py-4">
                    <Check size={17} className="mt-0.5 shrink-0 text-[var(--green)]" />
                    <span className="text-sm md:text-base text-[var(--ink)]/90">{p}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <div>
            <Reveal>
              <h2 className="mono text-xs tracking-[0.35em] uppercase text-[var(--green)] mb-8">Capacidades</h2>
            </Reveal>
            <div className="flex flex-wrap gap-3">
              {service.capabilities.map((c, i) => (
                <Reveal key={c} delay={i * 0.05}>
                  <span className="mono rounded-full border border-[var(--line)] bg-[var(--surface)] px-5 py-2.5 text-sm text-[var(--ink)]/80 transition-colors hover:border-[var(--cyan)]/50 hover:text-[var(--cyan)]">
                    {c}
                  </span>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <h2 className="mono text-xs tracking-[0.35em] uppercase text-[var(--green)] mt-14 mb-8">Cómo lo hacemos</h2>
            </Reveal>
            <FlowVisual steps={service.flow} />
          </div>
        </div>
      </section>

      <section className="py-24 text-center">
        <Reveal>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            ¿Hablamos de <span className="text-gradient">{service.title.toLowerCase()}</span>?
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="mt-8 flex justify-center">
            <MagneticButton onClick={openCalendly} testId="service-bottom-cta">Agendar una reunión <ArrowRight size={15} className="text-[var(--green)]" /></MagneticButton>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
